import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import type { ExternalToast } from '@spartan-ng/brain/sonner';
import { TranslateService } from '@ngx-translate/core';
import { extractApiError } from '@core/http/api-error';

export type NotifyId = string | number;

export interface NotifyOptions {
  /** Auto-close delay in ms. Default comes from the root `<hlm-toaster>` (5000). */
  duration?: number;
  /** Shows the "x" close button on this toast. */
  closeButton?: boolean;
  /** `false` blocks swipe-to-dismiss AND the close button (Sonner semantics). */
  dismissible?: boolean;
  /** Custom id. Re-using an id updates the toast in place instead of stacking a new one. */
  id?: NotifyId;
  /** Bold heading; when set, `message` is rendered as the body below it. */
  title?: string;
  /** Called when the user taps/clicks the toast body (the close button is ignored). */
  onClick?: () => void;
}

type NotifyKind = 'success' | 'error' | 'warning' | 'info';

/**
 * Single entry point for user notifications (toasts).
 *
 * This facade is the ONLY place that knows which toast library is used (Sonner
 * through `@spartan-ng/brain/sonner`). The `<hlm-toaster>` host lives once in
 * `AppComponent`.
 *
 * Sonner is loaded lazily (dynamic import) to keep it out of the initial bundle.
 * Calls are chained on the same promise, so order is preserved, and ids are
 * generated here so callers get one synchronously.
 */
@Injectable({ providedIn: 'root' })
export class NotifyService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly doc = inject(DOCUMENT);
  private readonly translate = inject(TranslateService);
  private readonly tapCleanups = new Map<NotifyId, () => void>();
  private tapSeq = 0;
  private idSeq = 0;
  private sonner?: Promise<typeof import('@spartan-ng/brain/sonner')>;

  success(message: string, options?: NotifyOptions): NotifyId {
    return this.show('success', message, options);
  }

  error(message: string, options?: NotifyOptions): NotifyId {
    return this.show('error', message, options);
  }

  /** Extracts an API response message or translates the supplied fallback key. */
  apiError(error: unknown, fallbackKey: string): NotifyId {
    const message = extractApiError(error, fallbackKey);
    return this.error(
      message === fallbackKey ? this.translate.instant(fallbackKey) : message,
    );
  }

  warning(message: string, options?: NotifyOptions): NotifyId {
    return this.show('warning', message, options);
  }

  info(message: string, options?: NotifyOptions): NotifyId {
    return this.show('info', message, options);
  }

  /** Dismisses one toast, or all of them when no id is given. */
  dismiss(id?: NotifyId): void {
    if (!this.isBrowser) return;
    if (id === undefined) {
      this.tapCleanups.forEach((cleanup) => cleanup());
      this.tapCleanups.clear();
    } else {
      this.releaseTap(id);
    }
    void this.load().then(({ toast }) => toast.dismiss(id));
  }

  private show(kind: NotifyKind, message: string, options: NotifyOptions = {}): NotifyId {
    // Toasts are a browser-only concern; Sonner keeps module-level state that
    // must never be shared between SSR requests.
    if (!this.isBrowser) return options.id ?? '';

    const { title, onClick, ...rest } = options;
    const id = options.id ?? `notify-${++this.idSeq}`;
    const data: ExternalToast = { ...rest, id };
    if (title) data.description = message;

    let tapKey: string | undefined;
    if (onClick) {
      tapKey = `notify-tap-${++this.tapSeq}`;
      data.testId = tapKey;
      // Remove the click listener once the toast is gone (user action or timeout).
      const release = () => this.releaseTap(id);
      data.onDismiss = release;
      data.onAutoClose = release;
    }

    if (onClick && tapKey) {
      this.releaseTap(id);
      this.bindTap(id, tapKey, onClick);
    }

    void this.load().then(({ toast }) => toast[kind](title || message, data));
    return id;
  }

  private load() {
    return (this.sonner ??= import('@spartan-ng/brain/sonner'));
  }

  private bindTap(id: NotifyId, tapKey: string, onClick: () => void): void {
    const handler = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target?.closest?.(`[data-testid="${tapKey}"]`)) return;
      if (target.closest('[data-close-button]')) return;
      onClick();
    };
    this.doc.addEventListener('click', handler);
    this.tapCleanups.set(id, () => this.doc.removeEventListener('click', handler));
  }

  private releaseTap(id: NotifyId): void {
    this.tapCleanups.get(id)?.();
    this.tapCleanups.delete(id);
  }
}
